/**
 * Settings → Locations → [Location] → Login & Access.
 *
 * The owner creates the credential by hand: a username, a password they choose,
 * and a link to write on the wall behind the counter. There is no invitation
 * and no reset email, because there is no mailbox and no person on the other
 * end — everything about the account is the owner's to set and to revoke.
 *
 * The one control that is absent is the location. A location login IS its
 * location; the API refuses to move one, and offering a picker here would be a
 * promise the server does not keep.
 */

import { useState, type ReactNode } from 'react';
import { Check, Copy, KeyRound, Link2, ShieldAlert, Store } from 'lucide-react';
import {
  LOCATION_GRANTABLE_PERMISSIONS,
  type LocationLoginView,
  type Permission,
} from '@cenox/shared';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  Input,
  Modal,
  Spinner,
} from '../../components/ui';
import { useAuth } from '../../lib/auth';
import { ApiError } from '../../lib/api';
import { dateTime } from '../../lib/format';
import { useModule } from '../../lib/modules';
import {
  useCreateLocationLogin,
  useDeleteLocationLogin,
  useLocationLogins,
  useResetLocationPassword,
  useUpdateLocationLogin,
} from './loginData';

export function LoginAccessDialog({
  branchId,
  branchName,
  onClose,
}: {
  branchId: string;
  branchName: string;
  onClose: () => void;
}): ReactNode {
  const { business, can } = useAuth();
  const timezone = business?.timezone ?? 'UTC';
  const logins = useLocationLogins(branchId);
  const login = logins.data?.data[0] ?? null;

  const mayManage = can('members:update');
  const mayCreate = can('members:invite');

  return (
    <Modal open onClose={onClose} title={`${branchName} — Login & Access`} size="lg">
      {logins.isPending ? (
        <Spinner label="Loading" />
      ) : logins.isError ? (
        <ErrorState message="Could not load this location's login." />
      ) : login ? (
        <ExistingLogin
          login={login}
          branchId={branchId}
          branchName={branchName}
          timezone={timezone}
          mayManage={mayManage}
          onClose={onClose}
        />
      ) : mayCreate ? (
        <CreateLogin branchId={branchId} branchName={branchName} />
      ) : (
        <EmptyState
          title="No login for this location"
          description="Ask the owner or an admin to create one."
        />
      )}
    </Modal>
  );
}

// ── Creating ────────────────────────────────────────────────────────────────

function CreateLogin({
  branchId,
  branchName,
}: {
  branchId: string;
  branchName: string;
}): ReactNode {
  const suggested = branchName.toLowerCase().replace(/[^a-z0-9]+/g, '').slice(0, 32);
  const [username, setUsername] = useState(suggested.length >= 3 ? suggested : '');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const create = useCreateLocationLogin(branchId);
  const fields = create.error instanceof ApiError ? create.error.fields : undefined;
  const mismatch = confirm.length > 0 && password !== confirm;

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (mismatch) return;
        create.mutate({ username, password, displayName: branchName });
      }}
    >
      <p className="text-sm text-slate-600">
        A shared login for whoever is working at <strong>{branchName}</strong>. It can only ever
        see {branchName} — that is fixed when the login is created and cannot be changed
        afterwards.
      </p>

      <Input
        label="Username"
        hint="What gets typed at the counter. Letters, numbers, dots and dashes."
        error={fields?.username?.[0]}
        required
        value={username}
        onChange={(e) => setUsername(e.target.value)}
        placeholder="holyghost"
        autoFocus
      />

      <Input
        label="Password"
        hint="You set it, and you tell whoever is at the counter. There is no reset email."
        error={fields?.password?.[0]}
        required
        type="password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
      />

      <Input
        label="Confirm password"
        error={mismatch ? 'The two passwords do not match' : undefined}
        required
        type="password"
        value={confirm}
        onChange={(e) => setConfirm(e.target.value)}
      />

      {create.error && !fields ? <ErrorState message={create.error.message} /> : null}

      <div className="flex justify-end">
        <Button type="submit" disabled={create.isPending || mismatch}>
          Create login
        </Button>
      </div>
    </form>
  );
}

// ── Managing ────────────────────────────────────────────────────────────────

function ExistingLogin({
  login,
  branchId,
  branchName,
  timezone,
  mayManage,
  onClose,
}: {
  login: LocationLoginView;
  branchId: string;
  branchName: string;
  timezone: string;
  mayManage: boolean;
  onClose: () => void;
}): ReactNode {
  const [resetting, setResetting] = useState(false);
  const update = useUpdateLocationLogin(branchId, login.id);
  const remove = useDeleteLocationLogin(branchId, login.id);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="font-mono text-base font-semibold text-slate-900">{login.username}</p>
          <p className="mt-0.5 text-xs text-slate-500">
            {login.lastLoginAt
              ? `Last signed in ${dateTime(login.lastLoginAt, timezone)}`
              : 'Never signed in'}
          </p>
        </div>
        <Badge tone={login.isActive ? 'success' : 'neutral'}>
          {login.isActive ? 'Active' : 'Disabled'}
        </Badge>
      </div>

      {/*
        The binding, stated rather than implied. Somebody looking for a way to
        move this login should find the answer here instead of hunting for a
        control that does not exist.
      */}
      <div className="flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600">
        <ShieldAlert className="mt-0.5 size-4 shrink-0 text-slate-400" aria-hidden />
        <span>
          Permanently scoped to <strong>{branchName}</strong>. It cannot see other locations or
          choose &ldquo;All locations&rdquo;, and the location cannot be changed — create a login
          for the other location instead.
        </span>
      </div>

      <LoginLink url={login.loginUrl} />

      <div>
        <p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-slate-500">
          What it can do
        </p>
        <div className="flex flex-wrap gap-1.5">
          {login.effectivePermissions.map((p) => (
            <span
              key={p}
              className="rounded-full bg-slate-100 px-2 py-0.5 font-mono text-xs text-slate-600"
            >
              {p}
            </span>
          ))}
        </div>
        <p className="mt-1.5 text-xs text-slate-400">
          Exactly what this login can do right now. Switching a module on never adds to it —
          taking sales is only ever allowed below. No reports, dashboard, settings or other
          locations — those cannot be granted to a location login.
        </p>
      </div>

      <TillAccess login={login} branchName={branchName} />

      {mayManage ? (
        <div className="flex flex-wrap justify-end gap-2 border-t border-slate-200 pt-3">
          <Button variant="secondary" onClick={() => setResetting(true)}>
            <KeyRound className="size-4" />
            Reset password
          </Button>
          <Button
            variant="secondary"
            disabled={update.isPending}
            onClick={() => update.mutate({ isActive: !login.isActive })}
          >
            {login.isActive ? 'Disable' : 'Enable'}
          </Button>
          <Button
            variant="danger"
            disabled={remove.isPending}
            onClick={() => remove.mutate(undefined, { onSuccess: onClose })}
          >
            Remove
          </Button>
        </div>
      ) : null}

      {resetting ? (
        <ResetPassword
          branchId={branchId}
          loginId={login.id}
          username={login.username}
          onClose={() => setResetting(false)}
        />
      ) : null}
    </div>
  );
}

/**
 * Allow Sales Till Access.
 *
 * Off unless the owner switches it on, for this login alone. Only the owner may
 * change it — the API refuses anybody else — and it does nothing while the
 * business has Sales switched off, which the panel says rather than leaving the
 * owner to wonder why the counter still cannot sell.
 */
function TillAccess({
  login,
  branchName,
}: {
  login: LocationLoginView;
  branchName: string;
}): ReactNode {
  const { business } = useAuth();
  const salesOn = useModule('sales_pos');
  const update = useUpdateLocationLogin(login.branchId, login.id);
  const isOwner = business?.role === 'owner';

  return (
    <div className="rounded-lg border border-slate-200 p-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-2">
          <Store className="mt-0.5 size-4 shrink-0 text-slate-400" aria-hidden />
          <div className="min-w-0">
            <p className="text-sm font-medium text-slate-900">Allow Sales Till Access</p>
            <p className="mt-0.5 text-xs text-slate-500">
              Lets this login take payment for sales at {branchName} and see {branchName}&rsquo;s
              sales, at the prices on file. It can never edit, void, delete or refund a sale,
              change a price, or see what anything cost.
            </p>
          </div>
        </div>
        <Badge tone={login.tillAccess ? 'success' : 'neutral'}>
          {login.tillAccess ? 'Allowed' : 'Off'}
        </Badge>
      </div>

      {login.tillAccess && !salesOn ? (
        <p className="mt-2 text-xs text-amber-700">
          Allowed, but Sales is switched off for this business, so the till is not available
          until Sales is turned on.
        </p>
      ) : null}

      {isOwner ? (
        <div className="mt-3 flex justify-end">
          <Button
            variant="secondary"
            size="sm"
            disabled={update.isPending}
            onClick={() => update.mutate({ tillAccess: !login.tillAccess })}
          >
            {login.tillAccess ? 'Turn off till access' : 'Allow till access'}
          </Button>
        </div>
      ) : (
        <p className="mt-2 text-xs text-slate-400">Only the owner can change this.</p>
      )}
      {update.error ? <ErrorState message={update.error.message} /> : null}
    </div>
  );
}

/** The link that goes on the wall. Copy is the whole point of showing it. */
function LoginLink({ url }: { url: string }): ReactNode {
  const [copied, setCopied] = useState(false);

  return (
    <div>
      <p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-slate-500">
        Sign-in link
      </p>
      <div className="flex items-center gap-2">
        <span className="min-w-0 flex-1 truncate rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 font-mono text-xs text-slate-600">
          {url}
        </span>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => {
            void navigator.clipboard?.writeText(url).then(
              () => {
                setCopied(true);
                window.setTimeout(() => setCopied(false), 2000);
              },
              // A browser that refuses clipboard access is not an error worth
              // a dialog — the link is on screen and selectable.
              () => undefined,
            );
          }}
        >
          {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>
      <p className="mt-1 flex items-center gap-1 text-xs text-slate-400">
        <Link2 className="size-3" aria-hidden />
        Opens a sign-in page that already knows the business and the username.
      </p>
    </div>
  );
}

function ResetPassword({
  branchId,
  loginId,
  username,
  onClose,
}: {
  branchId: string;
  loginId: string;
  username: string;
  onClose: () => void;
}): ReactNode {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [done, setDone] = useState<number | null>(null);
  const reset = useResetLocationPassword(branchId, loginId);
  const fields = reset.error instanceof ApiError ? reset.error.fields : undefined;
  const mismatch = confirm.length > 0 && password !== confirm;

  return (
    <Modal open onClose={onClose} title={`Reset the password for ${username}`}>
      {done !== null ? (
        <div className="space-y-4">
          <p className="text-sm text-slate-600">
            Done. {done === 0 ? 'Nobody was signed in.' : `${done} open session${done === 1 ? ' was' : 's were'} ended`}
            {done === 0 ? '' : ', so whoever was using the old password has been signed out.'} Tell
            the counter the new one.
          </p>
          <div className="flex justify-end">
            <Button onClick={onClose}>Close</Button>
          </div>
        </div>
      ) : (
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (mismatch) return;
            reset.mutate(password, { onSuccess: (r) => setDone(r.sessionsRevoked) });
          }}
        >
          <p className="text-sm text-slate-600">
            This signs out everyone currently using this login, which is usually the reason to
            change a shared password.
          </p>
          <Input
            label="New password"
            error={fields?.password?.[0]}
            required
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoFocus
          />
          <Input
            label="Confirm password"
            error={mismatch ? 'The two passwords do not match' : undefined}
            required
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
          {reset.error && !fields ? <ErrorState message={reset.error.message} /> : null}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={reset.isPending || mismatch}>
              Set password
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
}

/** Exported for the Locations list, which shows whether a login exists. */
export { LOCATION_GRANTABLE_PERMISSIONS };
export type { Permission };
